<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  hook: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const selectedInboxIds = ref([]);

const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const uiFlags = computed(() => store.getters['integrations/getUIFlags']);

const savedInboxIds = computed(() => {
  return (props.hook.settings?.inbox_ids || []).map(Number);
});

onMounted(() => {
  store.dispatch('inboxes/get');
  selectedInboxIds.value = savedInboxIds.value;
});

const toggleInbox = inboxId => {
  const id = Number(inboxId);
  if (selectedInboxIds.value.includes(id)) {
    selectedInboxIds.value = selectedInboxIds.value.filter(i => i !== id);
  } else {
    selectedInboxIds.value = [...selectedInboxIds.value, id];
  }
};

const saveInboxFilter = async () => {
  try {
    await store.dispatch(
      'integrations/updateSlackInboxes',
      selectedInboxIds.value
    );
    useAlert(t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.message || t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.UPDATE_ERROR')
    );
  }
};
</script>

<template>
  <div
    class="flex-1 w-full px-6 py-5 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
  >
    <div class="prose-lg max-w-5xl">
      <h5 class="text-n-slate-12 tracking-tight">
        {{ t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.TITLE') }}
      </h5>
      <p class="text-sm text-n-slate-11 mt-1 mb-4">
        {{ t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.DESCRIPTION') }}
      </p>
      <div class="flex flex-col gap-2 mb-4">
        <label
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            type="checkbox"
            :value="inbox.id"
            :checked="selectedInboxIds.includes(Number(inbox.id))"
            class="rounded border-n-slate-7"
            @change="toggleInbox(inbox.id)"
          />
          {{ inbox.name }}
        </label>
      </div>
      <p v-if="!inboxes.length" class="text-sm text-n-slate-11">
        {{ t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.NO_INBOXES') }}
      </p>
      <p class="text-xs text-n-slate-10 mb-4">
        {{ t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.ALL_INBOXES_NOTE') }}
      </p>
      <Button
        teal
        sm
        :is-loading="uiFlags.isUpdatingSlack"
        @click="saveInboxFilter"
      >
        {{ t('INTEGRATION_SETTINGS.SLACK.INBOX_FILTER.SAVE') }}
      </Button>
    </div>
  </div>
</template>
